import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface TabURLStateOptions {
  tabParamName?: string;
  defaultTab?: string;
}

// Store tab-specific parameters in sessionStorage
const getTabParams = (tabName: string): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem(`tab_params_${tabName}`);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const setTabParams = (tabName: string, params: Record<string, string>) => {
  try {
    sessionStorage.setItem(`tab_params_${tabName}`, JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to store tab parameters:', error);
  }
};

export const useTabURLState = (options: TabURLStateOptions = {}) => {
  const { tabParamName = 'tab', defaultTab = '' } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;
  
  // Get current tab from URL
  const getCurrentTab = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const currentTab = params.get(tabParamName);
    
    // If no tab parameter is found, return the default tab
    if (!currentTab) {
      return defaultTab;
    }
    
    return currentTab;
  }, [location.search, tabParamName, defaultTab]);

  // Switch to a different tab with tab-specific parameters
  const switchTab = useCallback((newTab: string) => {
    const currentTab = getCurrentTab();
    
    // If switching to the same tab, do nothing
    if (currentTab === newTab) {
      return;
    }
    
    // Store current tab's parameters before switching
    const currentParams = new URLSearchParams(location.search);
    const currentTabParams: Record<string, string> = {};
    currentParams.forEach((value, key) => {
      if (key !== tabParamName && value !== '' && value !== null && value !== undefined) {
        currentTabParams[key] = value;
      }
    });
    setTabParams(currentTab, currentTabParams);
    
    // Load the new tab's parameters
    const newTabParams = getTabParams(newTab);
    
    // Create new URL with new tab and its stored parameters
    const params = new URLSearchParams();
    params.set(tabParamName, newTab);
    
    // Add the new tab's stored parameters
    Object.entries(newTabParams).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params.set(key, value);
      }
    });
    
    const newURL = `${location.pathname}?${params.toString()}`;
    navigate(newURL, { replace: true });
  }, [getCurrentTab, location.search, location.pathname, navigate, tabParamName]);

  // Function to update URL parameters for the current tab
  const updateTabParams = useCallback((newParams: Record<string, string>) => {
    // A delayed list sync may run after a preview opens or closes; use the latest URL.
    const currentLocation = locationRef.current;
    const params = new URLSearchParams(currentLocation.search);
    const currentTab = params.get(tabParamName) || defaultTab;
    
    // Update parameters while preserving the current tab
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    
    // Store the updated parameters for this tab
    const currentTabParams: Record<string, string> = {};
    params.forEach((value, key) => {
      if (key !== tabParamName && value !== '' && value !== null && value !== undefined) {
        currentTabParams[key] = value;
      }
    });
    setTabParams(currentTab, currentTabParams);
    
    // Updating list pagination/filters must not discard a document preview deep link.
    const newURL = `${currentLocation.pathname}?${params.toString()}${currentLocation.hash}`;
    if (newURL !== `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`) {
      navigate(newURL, { replace: true });
    }
  }, [defaultTab, navigate, tabParamName]);

  // Function to clear all parameters except tab
  const clearTabParams = useCallback(() => {
    const currentTab = getCurrentTab();
    const params = new URLSearchParams();
    params.set(tabParamName, currentTab);
    
    // Clear stored parameters for this tab
    setTabParams(currentTab, {});
    
    const newURL = `${location.pathname}?${params.toString()}`;
    navigate(newURL, { replace: true });
  }, [getCurrentTab, location.pathname, navigate, tabParamName]);

  return {
    currentTab: getCurrentTab(),
    switchTab,
    updateTabParams,
    clearTabParams,
  };
}; 
